import request from './request'

export interface LoginParams {
  email?: string
  phone?: string
  password: string
}

export interface LoginResponse {
  userInfo: UserInfo
  token: string
}

export interface RegisterParams {
  email?: string
  phone?: string
  password: string
  verifyCode: string
  inviteCode?: string
}

export interface UserInfo {
  id: string
  phone: string
  email: string
  username: string
  nickname: string
  avatar: string
  reduceAiQuota: number
  plagiarismCount: number
  inviteCode: string
  role: number
  status: number
  createdAt: string
  updatedAt: string
  // 被邀请人奖励（我作为被邀请人获得的奖励）
  inviteeRewardReduceAiCount?: number
  inviteeRewardPlagiarismCount?: number
  // 邀请人信息（我作为被邀请人）
  inviterId?: string
  inviterInviteCode?: string
}

export interface UserMembershipInfo {
  id: string
  userId: string
  skuId: number
  orderId: string
  membershipLevel: string  // 如 "MEMBERSHIP_LEVEL_BRONZE"
  purchaseTime: string
  expireTime: string
  initialQuota: number
  remainingQuota: number
  status: string  // 如 "MEMBERSHIP_STATUS_ACTIVE"
  isUnlimited: boolean
  maxWordCount: number
  source: string  // 如 "MEMBERSHIP_SOURCE_PURCHASE"
  createdAt: string
  updatedAt: string
}

export interface UserInfoResponse {
  userInfo: UserInfo
  userMembershipInfos: UserMembershipInfo[]
}

// 验证码类型：1=注册, 2=找回密码, 3=修改密码
export const VerifyCodeType = {
  REGISTER: 1,
  RETRIEVE: 2,
  UPDATE_PASSWORD: 3
} as const

export interface GenerateVerifyCodeParams {
  type: number
  phone?: string
  email?: string
}

export interface RetrievePasswordParams {
  phone?: string
  email?: string
  code: string
  newPassword: string
}

/**
 * 用户登录
 */
export const login = (params: LoginParams) => {
  return request.post<LoginResponse>('/users/login', params)
}

/**
 * 用户注册
 */
export const register = (params: RegisterParams) => {
  return request.post('/users/register', params)
}

/**
 * 获取用户信息
 */
export const getUserInfo = () => {
  return request.get<UserInfoResponse>('/users/info')
}

/**
 * 获取验证码
 */
export const generateVerifyCode = (params: GenerateVerifyCodeParams) => {
  return request.post<{ code: string }>('/verify_code/generate', params)
}

/**
 * 找回密码
 */
export const retrievePassword = (params: RetrievePasswordParams) => {
  return request.post('/users/retrieve', params)
}

export interface UpdatePasswordParams {
  oldPassword: string
  newPassword: string
  code?: string
}

/**
 * 修改密码
 */
export const updatePassword = (params: UpdatePasswordParams) => {
  return request.put('/users/password', params)
}

// 会员状态
export const MembershipStatus = {
  UNSPECIFIED: 'MEMBERSHIP_STATUS_UNSPECIFIED',
  INACTIVE: 'MEMBERSHIP_STATUS_INACTIVE',
  ACTIVE: 'MEMBERSHIP_STATUS_ACTIVE',
  EXPIRED: 'MEMBERSHIP_STATUS_EXPIRED',
  EXHAUSTED: 'MEMBERSHIP_STATUS_EXHAUSTED',
} as const

// 会员等级
export const MembershipLevel = {
  NONE: 'MEMBERSHIP_LEVEL_NONE',
  BRONZE: 'MEMBERSHIP_LEVEL_BRONZE',
  SILVER: 'MEMBERSHIP_LEVEL_SILVER',
  GOLD: 'MEMBERSHIP_LEVEL_GOLD',
  DIAMOND: 'MEMBERSHIP_LEVEL_DIAMOND',
} as const

// 会员来源
export const MembershipSource = {
  PURCHASE: 'BUSINESS_TYPE_PURCHASE',
  REDEEM: 'BUSINESS_TYPE_REDEEM',
  GIFT_RECEIVE: 'BUSINESS_TYPE_GIFT_RECEIVE',
  ADMIN_GRANT: 'BUSINESS_TYPE_ADMIN_GRANT',
} as const

export interface GetUserMembershipParams {
  membershipStatus?: string  // 可选，按状态筛选
}

export interface GetUserMembershipResponse {
  userMembershipInfos: UserMembershipInfo[]
}

/**
 * 获取用户会员套餐列表
 */
export const getUserMembership = (params?: GetUserMembershipParams) => {
  return request.get<GetUserMembershipResponse>('/users/membership', { params })
}

// 邀请记录相关
export interface InvitationRecordInfo {
  id: string
  inviteeId: string
  inviteePhone?: string
  inviteeEmail?: string
  inviteePaymentSkuid?: number           // 购买的商品SKUID
  inviteePaymentProductName?: string     // 购买的商品名称
  inviterRewardReduceAiCount?: number    // 邀请人获得的降AI次数
  inviterRewardPlagiarismCount?: number  // 邀请人获得的查重次数
  createdAt: string
  updatedAt: string
}

export interface ListInvitationRecordsParams {
  'pageInfo.page'?: string
  'pageInfo.pageSize'?: string
}

export interface ListInvitationRecordsResponse {
  records: InvitationRecordInfo[]
  totalCount: number
}

/**
 * 获取邀请记录列表
 */
export const listInvitationRecords = (params?: ListInvitationRecordsParams) => {
  return request.get<ListInvitationRecordsResponse>('/users/invitation_records', { params })
}

